import { SchmancyElement } from '@schmancy/mixins/index'
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { Observable } from 'rxjs'
import { LazyComponent } from './lazy'

export type ObservableGuardResult = Observable<boolean>

export type RouteComponent =
	| string // Tag name
	| CustomElementConstructor // Constructor function
	| HTMLElement // Existing element
	| LazyComponent<CustomElementConstructor>

export interface RouteConfig {
	// Either an exact segment name matched anywhere in the path (`'admin'`), or a
	// dynamic path pattern — one or more `/`-joined segments where any segment
	// starting with `:` captures that position (`'/:eventId'`, `'/events/:eventId'`)
	// — matched position-by-position against the whole pathname and merged onto
	// the resolved route's `params`. A `when` is a pattern only when one of its
	// `/`-separated segments STARTS with `:`; a colon anywhere else (`'checkout:step'`)
	// names no parameter and stays an exact name, so every route that never wrote a
	// `:param` segment keeps the original exact-match behaviour unchanged. See
	// `matchPathPattern`/`buildPathFromPattern` in `utils.ts`.
	//
	// SCOPE: an area nested in a page another area mounted, or one declaring a
	// `base`, reads only the segments after its scope, matched from the first of
	// them — a pattern as a prefix, its captures reaching `params` — so an id in
	// the address above cannot be read as a route name (`SchmancyArea.routeAt`).
	// What follows describes an area with no scope.
	//
	// RESOLUTION ORDER, on cold load and on a browser traversal alike: an exact
	// `when` equal to any segment of the pathname wins over every pattern,
	// whatever order the routes are declared in. Only once no exact name matches
	// are patterns tried, and then in DECLARATION (DOM) order — the first
	// `<schmancy-route>` whose pattern matches the whole pathname wins, so two
	// patterns that can both match one address are disambiguated by which one is
	// written first, never by which is more specific.
	//
	// A push naming no explicit `path` builds the concrete address straight from
	// the pattern and the pushed `params` — no JSON-blob fallback — but only for
	// the area named by `AreaService.addressArea` (default `'main'`), mirroring
	// `prettyURL`'s one-readable-area design; a dynamic-pattern route on any
	// other area still needs an explicit `path` to get a clean URL
	// (`AreaService.createCleanURL` in `area.service.ts`). That address is built
	// from the ROOT, so an app served under a sub-path (`/app/…`) does not keep
	// its prefix across a pattern route.
	//
	// THE PRICE, WHILE THE ADDRESS AREA SHOWS A PATTERN ROUTE: the URL is that
	// pattern's path and nothing else, so no OTHER area's route appears in it.
	// Their routes are still written into `history.state`, so back, forward and
	// reload restore them exactly; a link typed, pasted or shared from that
	// address is what loses them, and cold-loads those areas from their own
	// `when`/`.default` matching instead. `prettyURL` makes the same trade behind
	// its own flag — here the trade begins the moment a `:param` route is declared
	// on the address area, and it applies app-wide for as long as that route is
	// the one mounted there.
	//
	// A param value the address cannot carry — absent, empty, `.`, `..`, or a lone
	// surrogate — falls back to the prettyURL/JSON-blob address rather than
	// writing a path that names a different route. The route still mounts and
	// still receives its `params`; only the clean URL is forfeited.
	when: string
	// Retired names this route still answers to — an old segment in a bookmarked
	// address, an encoded segment written before a rename, a push from code not yet
	// migrated. Each resolves to this route and is recorded under `when`; a cold
	// address carrying one has the alias's matched span replaced by `when`, a
	// pattern filled in from what the alias captured. An alias may be a pattern
	// itself; one that leaves a param the route's pattern needs uncaptured is refused.
	aliases?: string[]
	component: RouteComponent
	exact?: boolean
	guard?: ObservableGuardResult
}

export type SchmancyRouteRedirectEvent = CustomEvent<{
	blockedRoute: string
	area: string
	params: Record<string, string>
	state: Record<string, unknown>
	redirectTarget?: unknown
}>

// Renders nothing — schmancy-area reads its slotted <schmancy-route> children
// for route config; this element only carries the config as properties.
//
// Fires `redirect` (SchmancyRouteRedirectEvent) when the parent area evaluates
// `guard` as false. Listen with `@redirect`.
@customElement('schmancy-route')
export class SchmancyRoute extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: none;
			}
		`,
	]

	// Exact segment name, or a dynamic `:param` path pattern — see `RouteConfig.when`.
	@property({ type: String })
	when!: string

	/** Retired names this route still answers to — see `RouteConfig.aliases`. */
	@property({ type: Array })
	aliases: string[] = []

	@property({ type: Object })
	component!: RouteComponent

	@property({ type: Boolean })
	exact?: boolean = false

	@property({ type: Object })
	guard?: ObservableGuardResult

	getConfig(): RouteConfig {
		return {
			when: this.when,
			aliases: this.aliases,
			component: this.component,
			exact: this.exact,
			guard: this.guard,
		}
	}

	render() {
		return html``
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-route': SchmancyRoute
	}
}
